import React, { useState } from 'react';
import { Mail, Copy, Check, Send, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';

const SAMPLE = "Team, the owner has directed us to install two additional rooftop condenser units and associated pipework for the new server room. These were not shown on the original mechanical drawings. Please advise cost and programme impact.";

/**
 * Forward-to-Recoup: shows the project's unique inbound email address so a PM can
 * simply forward site emails in. Includes a functional test-forward action.
 */
export const ForwardEmailCard = ({ address, onCaptured }) => {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(address); } catch (e) { /* noop */ }
    setCopied(true);
    toast.success('Address copied');
    setTimeout(() => setCopied(false), 1800);
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post('/inbound/email/sync', {
        to: address, from: 'you@site.com',
        subject: 'FW: Test - additional rooftop condensers', text: SAMPLE,
      });
      if (r.data.opportunity_found) toast.success('Test email captured — opportunity found!');
      else if (r.data.ingested) toast('Test email captured and reviewed.');
      else toast.message(r.data.note || 'Sent.');
      onCaptured && onCaptured();
    } catch (e) { toast.error('Could not send test email'); }
    finally { setSending(false); }
  };

  if (!address) return null;
  return (
    <Card className="p-5" data-testid="forward-email-card">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-brand-50 ring-1 ring-brand-100 p-2.5 shrink-0"><Mail className="h-5 w-5 text-brand-600" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink-900">Forward emails here</div>
          <p className="text-xs text-slate-500 mt-0.5">Forward any site email to this address and Recoup files it under this project automatically.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="flex-1 min-w-0 truncate rounded-lg bg-slate-50 ring-1 ring-slate-200 px-3 py-2 text-sm font-mono text-slate-700" data-testid="inbound-address">{address}</code>
            <Button variant="outline" size="sm" className="h-9" onClick={copy} data-testid="copy-inbound-btn">
              {copied ? <Check className="h-4 w-4 text-money-600" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
            <Button size="sm" className="h-9 bg-primary hover:bg-ink-700" onClick={sendTest} disabled={sending} data-testid="send-test-email-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span className="ml-1.5">Send test</span>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
